import { useEffect, useRef, useState, useCallback } from "react";
import { useQueryClient } from "@tanstack/react-query";
import {
  deviceBridgeService,
  type ConnectionMode,
  type BridgeResponse,
} from "../services/deviceBridge.service";

interface BridgeEvent {
  type: string;
  messageType?: string;
  payload: Record<string, unknown>;
  timestamp?: string;
}

/**
 * Hook that manages real-time bridge status using local-first connection.
 *
 * Connection priority:
 *   1. ws://localhost:8765 — direct to local bridge (fast, no backend dependency)
 *   2. wss://backend/lis/bridge/ws/frontend — backend relay (fallback)
 */
export const useBridgeStatus = () => {
  const queryClient = useQueryClient();
  const [connected, setConnected] = useState(false);
  const [connectionMode, setConnectionMode] =
    useState<ConnectionMode>("disconnected");
  const [lastEvent, setLastEvent] = useState<BridgeEvent | null>(null);
  const cleanupRef = useRef<Array<() => void>>([]);

  const handleMessage = useCallback(
    (data: BridgeResponse) => {
      // Normalize: local bridge uses `type`, backend relay uses `messageType`
      const raw = data as unknown as Record<string, unknown>;
      const eventType = raw.messageType ?? raw.type;
      const event: BridgeEvent = {
        type: String(eventType ?? ""),
        messageType: String(eventType ?? ""),
        payload: (data.payload as Record<string, unknown>) ?? {},
        timestamp: raw.timestamp as string | undefined,
      };
      setLastEvent(event);

      // Invalidate relevant queries based on event type
      const mt = event.type;
      switch (mt) {
        case "workstation:online":
        case "workstation:offline":
          queryClient.invalidateQueries({
            queryKey: ["bridge-workstations"],
          });
          if (event.payload?.workstationId) {
            queryClient.invalidateQueries({
              queryKey: ["bridge-workstation", event.payload.workstationId],
            });
          }
          break;

        case "device:connected":
        case "device:disconnected":
        case "device:error":
        case "PRINTER_STATUS":
          queryClient.invalidateQueries({
            queryKey: ["bridge-workstations"],
          });
          if (event.payload?.workstationId) {
            queryClient.invalidateQueries({
              queryKey: ["bridge-workstation", event.payload.workstationId],
            });
            queryClient.invalidateQueries({
              queryKey: ["bridge-devices", event.payload.workstationId],
            });
          }
          break;

        case "command:completed":
        case "command:failed":
        case "PRINT_RESULT":
          if (event.payload?.commandId) {
            queryClient.invalidateQueries({
              queryKey: ["bridge-command", event.payload.commandId],
            });
          }
          break;

        case "device:event":
        case "DOCUMENT_SCANNED":
          if (event.payload?.workstationId) {
            queryClient.invalidateQueries({
              queryKey: ["bridge-events", event.payload.workstationId],
            });
          }
          break;

        default:
          break;
      }
    },
    [queryClient]
  );

  useEffect(() => {
    // Subscribe to connection changes
    const unsubConn = deviceBridgeService.onConnectionChange(
      (mode: ConnectionMode) => {
        setConnectionMode(mode);
        setConnected(mode !== "disconnected");
      }
    );

    // Subscribe to messages
    const unsubMsg = deviceBridgeService.onMessage(handleMessage);

    // Start connection (tries local first, falls back to backend)
    deviceBridgeService.connect();

    cleanupRef.current = [unsubConn, unsubMsg];

    return () => {
      cleanupRef.current.forEach((fn) => fn());
      deviceBridgeService.disconnect();
    };
  }, [handleMessage]);

  return { connected, connectionMode, lastEvent };
};
